import { useRef, useState } from "react";
import { CameraIcon, UploadIcon } from "@phosphor-icons/react";
import { ErrorBanner } from "../crm/Form";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic"];
const MAX_BYTES = 15 * 1024 * 1024;
const MAX_PHOTOS = 30;

export default function PhotoUploader({
  workOrderId,
  api,
  onUploaded,
  disabled,
  currentCount = 0,
}) {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [queue, setQueue] = useState([]); // [{name, status: 'pending'|'uploading'|'ok'|'err', err?}]
  const [err, setErr] = useState("");

  const validateAndUpload = async (files) => {
    setErr("");
    const arr = Array.from(files || []);
    if (!arr.length) return;
    const remaining = Math.max(0, MAX_PHOTOS - currentCount);
    if (arr.length > remaining) {
      setErr(
        `Photo limit ${MAX_PHOTOS} reached — only ${remaining} more allowed on this report.`
      );
      return;
    }

    const next = arr.map((f) => ({ name: f.name, status: "pending", file: f }));
    setQueue((q) => [...q, ...next]);

    for (const item of next) {
      const f = item.file;
      // client-side sanity
      if (!ALLOWED_TYPES.includes(f.type)) {
        setQueue((q) =>
          q.map((x) =>
            x === item
              ? { ...x, status: "err", err: `Unsupported type ${f.type}` }
              : x
          )
        );
        continue;
      }
      if (f.size > MAX_BYTES) {
        setQueue((q) =>
          q.map((x) =>
            x === item
              ? { ...x, status: "err", err: "Too large (>15 MB)" }
              : x
          )
        );
        continue;
      }
      setQueue((q) =>
        q.map((x) => (x === item ? { ...x, status: "uploading" } : x))
      );
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(
          `/work-orders/${workOrderId}/diagnostic-report/photos`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setQueue((q) =>
          q.map((x) => (x === item ? { ...x, status: "ok" } : x))
        );
        onUploaded(data);
      } catch (e2) {
        const msg =
          e2?.response?.data?.detail || e2?.message || "Upload failed";
        setQueue((q) =>
          q.map((x) => (x === item ? { ...x, status: "err", err: msg } : x))
        );
      }
    }
    // Clear successful entries shortly after
    setTimeout(() => {
      setQueue((q) => q.filter((x) => x.status !== "ok"));
    }, 1500);
  };

  return (
    <div data-testid="photo-uploader" className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/heic"
          className="hidden"
          data-testid="photo-file-input"
          onChange={(e) => validateAndUpload(e.target.files)}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          data-testid="photo-camera-input"
          onChange={(e) => validateAndUpload(e.target.files)}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          data-testid="photo-pick-button"
          className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          <UploadIcon size={14} weight="bold" />
          Add Photos
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => cameraRef.current?.click()}
          data-testid="photo-camera-button"
          className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 sm:hidden"
        >
          <CameraIcon size={14} weight="bold" />
          Take Photo
        </button>
        <div className="text-[11px] text-slate-500 self-center">
          {currentCount} / {MAX_PHOTOS} · max 15 MB · jpeg / png / webp / heic
        </div>
      </div>
      <ErrorBanner message={err} />
      {queue.length > 0 && (
        <ul className="text-xs space-y-1" data-testid="photo-queue">
          {queue.map((q, i) => (
            <li
              key={i}
              className={`flex items-center gap-2 ${
                q.status === "err"
                  ? "text-red-700"
                  : q.status === "ok"
                    ? "text-emerald-700"
                    : "text-slate-600"
              }`}
            >
              <span className="inline-block w-2 h-2 rounded-full bg-current opacity-60" />
              <span className="truncate">{q.name}</span>
              <span className="ml-auto text-[10px] uppercase tracking-wider font-semibold">
                {q.status}
                {q.err ? ` · ${q.err}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
